'use client'

import { LineCheckCircle } from '@qr-front/icons/line-check-circle'
import { LineClose } from '@qr-front/icons/line-close'
import { LineDownload } from '@qr-front/icons/line-download'
import { LinePauseCircle } from '@qr-front/icons/line-pause-circle'
import { LineRefreshCw } from '@qr-front/icons/line-refresh-cw'
import { LineTrashAlt } from '@qr-front/icons/line-trash-alt'
import { Button } from '@qr-front/ui/components/Button'
import { useSidebar } from '@qr-front/ui/dashboard/components/Sidebar'
import { useBreakpoint } from '@qr-front/ui/hooks'
import { useAtom, useAtomValue } from 'jotai'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

import {
	bulkSelectedQrsState,
	confirmationDialogTypeState,
} from '../dialogsState'
import { TABLE_ACTION_TYPE } from '../TableActions/Actions/actionOptions'
import { withExpiredDialogIfNotAllowed } from '../TableActions/Actions/tableActionUtils'
import { tableAtom } from '../tableState'
import { BulkDownloadModal } from './BulkDownloadModal'
import { useBulkDownload } from './useBulkDownload'

export const BulkActionsToolbar = () => {
	const t = useTranslations()
	const table = useAtomValue(tableAtom)
	const [, setConfirmationDialogType] = useAtom(confirmationDialogTypeState)
	const [, setBulkSelectedQrs] = useAtom(bulkSelectedQrsState)
	const isDesktop = useBreakpoint('desktop')
	const isLargeDesktop = useBreakpoint('large-desktop')
	const { state: sidebarState } = useSidebar()
	const visibleSelectedRows = table
		.getFilteredRowModel()
		.rows.filter((row) => row.getIsSelected())
	const selectedQrs = visibleSelectedRows.map((row) => row.original)
	const visibleSelectedCount = selectedQrs.length
	const totalFilteredCount = table.getFilteredRowModel().rows.length
	const isVisible = visibleSelectedCount > 0
	const allDisabled = selectedQrs.every((qr) => qr.disabled)
	const allActive = selectedQrs.every((qr) => !qr.disabled)
	const showPauseButton = allActive
	const showActivateButton = allDisabled
	const showSelectAllButton =
		visibleSelectedCount > 0 && visibleSelectedCount < totalFilteredCount

	const [headerBottom, setHeaderBottom] = useState(64)

	useEffect(() => {
		const update = () => {
			const header = document.querySelector('header')
			if (header) {
				setHeaderBottom(Math.max(0, header.getBoundingClientRect().bottom))
			}
		}
		update()
		window.addEventListener('scroll', update, { passive: true })
		return () => window.removeEventListener('scroll', update)
	}, [])

	const {
		downloadState,
		downloadSelectedQrCodes,
		cancelDownload,
		resetDownloadState,
	} = useBulkDownload()

	const handleDeselectAll = () => {
		table.resetRowSelection()
	}

	const handleSelectAll = () => {
		const allFilteredRowIds = table
			.getFilteredRowModel()
			.rows.reduce((acc, row) => ({ ...acc, [row.id]: true }), {})
		table.setRowSelection(allFilteredRowIds)
	}

	const handleDownload = withExpiredDialogIfNotAllowed(async () => {
		await downloadSelectedQrCodes(selectedQrs)
	})

	const handlePause = withExpiredDialogIfNotAllowed(() => {
		setBulkSelectedQrs(selectedQrs)
		setConfirmationDialogType(TABLE_ACTION_TYPE.BulkPause)
	})

	const handleActivate = withExpiredDialogIfNotAllowed(() => {
		setBulkSelectedQrs(selectedQrs)
		setConfirmationDialogType(TABLE_ACTION_TYPE.BulkActivate)
	})

	const handleResetScans = withExpiredDialogIfNotAllowed(() => {
		setBulkSelectedQrs(selectedQrs)
		setConfirmationDialogType(TABLE_ACTION_TYPE.BulkResetScans)
	})

	const handleDelete = () => {
		setBulkSelectedQrs(selectedQrs)
		setConfirmationDialogType(TABLE_ACTION_TYPE.BulkDelete)
	}

	if (!isVisible) {
		return null
	}

	const sidebarWidth =
		sidebarState === 'expanded'
			? 'var(--sidebar-width)'
			: 'var(--sidebar-width-icon)'

	return (
		<div
			className="fixed bottom-0 z-10 mx-auto w-full bg-white shadow-generator"
			style={{
				left: isDesktop ? sidebarWidth : '0',
				maxWidth: isDesktop ? `calc(100dvw - ${sidebarWidth})` : '100%',
			}}
		>
			<div className="w-full px-6 py-4">
				<div className="relative mx-auto flex max-w-cabinet-screen-constrained items-center justify-between gap-4">
					<div
						className="desktop:top-unset type-body-small fixed inset-x-0 flex items-center justify-center gap-4 bg-white/80 px-5 py-1 backdrop-blur-2xl tablet:px-8 desktop:static"
						style={{ top: isDesktop ? undefined : headerBottom }}
					>
						<span>
							{t('dashboard__qr_table__items_selected', {
								size: visibleSelectedCount || 0,
								count: visibleSelectedCount,
							})}
						</span>
						{showSelectAllButton && (
							<button
								onClick={handleSelectAll}
								className="type-body-small cursor-pointer text-blue underline hover:opacity-80"
							>
								{t('dashboard__qr_table__select_all_items', {
									size: totalFilteredCount,
									count: totalFilteredCount,
								})}
							</button>
						)}
					</div>
					<div className="absolute left-1/2 flex -translate-x-1/2 items-center justify-center gap-2">
						<Button visual="soft-shadow" onClick={handleDownload}>
							<LineDownload className="size-4" />
							{isLargeDesktop && (
								<span>
									{t('dashboard__qr_table__qr_card__download_option__download')}
								</span>
							)}
						</Button>

						{showPauseButton && (
							<Button visual="soft-shadow" onClick={handlePause}>
								<LinePauseCircle className="size-4" />
								{isLargeDesktop && (
									<span>
										{t('dashboard__qr_table__qr_card__download_option__pause')}
									</span>
								)}
							</Button>
						)}

						{showActivateButton && (
							<Button visual="soft-shadow" onClick={handleActivate}>
								<LineCheckCircle className="size-4" />
								{isLargeDesktop && (
									<span>
										{t(
											'dashboard__qr_table__qr_card__download_option__activate',
										)}
									</span>
								)}
							</Button>
						)}

						<Button visual="soft-shadow" onClick={handleResetScans}>
							<LineRefreshCw className="size-4" />
							{isLargeDesktop && (
								<span>
									{t(
										'dashboard__qr_table__qr_card__download_option__reset_scans',
									)}
								</span>
							)}
						</Button>

						<Button
							visual="soft-shadow"
							onClick={handleDelete}
							className="border-error text-error hover:border-error hover:text-error"
						>
							<LineTrashAlt className="size-4 text-error" />
							{isLargeDesktop && (
								<span>
									{t('dashboard__qr_table__qr_card__download_option__delete')}
								</span>
							)}
						</Button>
					</div>
					<Button
						visual="text"
						onClick={handleDeselectAll}
						className="ml-auto h-10"
						aria-label="Deselect all"
					>
						<LineClose className="size-4" />
					</Button>
				</div>
			</div>

			<BulkDownloadModal
				isOpen={downloadState.isDownloading || downloadState.isComplete}
				downloadState={downloadState}
				onClose={resetDownloadState}
				onCancel={cancelDownload}
			/>
		</div>
	)
}
